import { InnerButton } from '@/modules/InnerButton';
import { ModalFooterProps, ModalButton } from '@/shared/types/modal';
import { useModalContext } from '../../context';
import { cn } from '@/shared/utils/cn';
import { InnerButtonProps } from '@/shared/types/button';

/**
 * Extrae las props de ModalButton que son válidas para InnerButton
 * Excluye las props especiales del Modal (id, closeOnClick, show, label)
 */
const getInnerButtonProps = (
  button: ModalButton,
  overrides?: Partial<InnerButtonProps>,
): Partial<InnerButtonProps> => {
  const { id, closeOnClick, show, label, onClick, ...innerButtonProps } = button;
  return {
    ...innerButtonProps,
    ...overrides,
  };
};

export const ModalFooter = ({
  children,
  footerAuxButton: footerAuxButtonProp,
  footerButtons: footerButtonsProp,
  // Props legacy (mantener para retrocompatibilidad)
  footerButtonIcon,
  footerButtonIconDuotonePrimary,
  footerButtonIconDuotoneSecondary,
  footerButtonIconDuotoneOpacityPrimary,
  footerButtonIconDuotoneOpacitySecondary,
  footerButtonOnClick,
}: ModalFooterProps) => {
  const {
    onClose,
    // Nuevo sistema procesado
    processedFooterAuxButton,
    processedFooterButtons,
    sectionPadding = 'md',
  } = useModalContext();

  const sectionPaddingClass = sectionPadding === 'sm' ? 'ui-p-8' : 'ui-p-16';

  // Usar props directas si están disponibles, sino usar las procesadas del contexto
  const footerAuxButtonToRender = footerAuxButtonProp || processedFooterAuxButton;
  const buttonsToRender = footerButtonsProp || processedFooterButtons || [];

  // Handler genérico para botones del nuevo sistema
  const handleButtonClick = (
    button: (typeof buttonsToRender)[0],
    event: React.MouseEvent<HTMLElement>,
  ) => {
    // Ejecutar el onClick del botón si existe
    if (button.onClick) {
      button.onClick(event as React.MouseEvent<HTMLButtonElement>);
    }
    // Cerrar el modal si closeOnClick está habilitado
    if (button.closeOnClick) {
      onClose();
    }
  };

  // Handler para el botón auxiliar
  const handleAuxButtonClick = (event?: React.MouseEvent<HTMLElement>) => {
    if (footerAuxButtonToRender?.onClick) {
      footerAuxButtonToRender.onClick(event as React.MouseEvent<HTMLButtonElement>);
    }
    if (footerAuxButtonToRender?.closeOnClick) {
      onClose();
    }
  };

  return (
    <div
      className={cn(
        'ui-flex ui-rounded-b-lg ui-border-t ui-border-default',
        sectionPaddingClass,
        footerAuxButtonToRender ? 'ui-justify-between' : 'ui-justify-end',
        'ui-items-center ui-gap-2',
      )}
    >
      {/* Botón auxiliar del footer (a la izquierda) */}
      {footerAuxButtonToRender && (
        <InnerButton
          aria-label={footerAuxButtonToRender.label || 'Footer aux button'}
          {...getInnerButtonProps(footerAuxButtonToRender, {
            theme: footerAuxButtonToRender.theme || 'solid',
            pill: footerAuxButtonToRender.pill || 'pill',
            color: footerAuxButtonToRender.color || 'secondary',
            icon: footerAuxButtonToRender.icon || footerAuxButtonToRender.leftSlot,
            size: footerAuxButtonToRender.size || 'xs',
            onClick: handleAuxButtonClick,
          })}
        >
          {footerAuxButtonToRender.label}
        </InnerButton>
      )}

      {children ? (
        children
      ) : (
        <div className="ui-flex ui-gap-12">
          {buttonsToRender.map((button, index) => {
            // Filtrar botones que no deben mostrarse
            if (button.show === false) {
              return null;
            }

            return (
              <InnerButton
                key={button.id || `footer-button-${index}`}
                {...getInnerButtonProps(button, {
                  color: button.color || 'primary',
                  theme: button.theme || 'solid',
                  size: button.size || 'xs',
                  pill: button.pill || 'sm',
                  onClick: (event) => handleButtonClick(button, event),
                })}
              >
                {button.label}
              </InnerButton>
            );
          })}
        </div>
      )}
    </div>
  );
};
